<template>
  <div class="px-page mb-8">
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-semibold leading-tight"> Custom {{ customsType }} </h2>
      <a :href="`/api/custom-orders/${type}/export`" class="btn btn-primary btn-lg"> Export </a>
    </div>
  </div>
  <div class="card px-page">
    <div class="flex items-center justify-between border-b border-gray-200">
      <nav class="-mb-px flex flex-1 space-x-6 xl:space-x-8" aria-label="Tabs">
        <span v-for="tab in tabs" :key="tab.name" :class="[tab.current ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:border-primary hover:text-primary', 'cursor-pointer whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium']" @click="selectStatus(tab.slug)">
          {{ tab.name }}
        </span>
      </nav>
      <div class="">
        <Listbox v-model="selectedFilter" as="div" class="flex items-center gap-3" @update:model-value="(value) => selectFilter(value)">
          <ListboxLabel class="block text-sm text-gray-700">{{ customsType }} Filter</ListboxLabel>
          <div class="relative w-36">
            <ListboxButton class="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6">
              <span class="block truncate">{{ selectedFilter.label }}</span>
              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>

            <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
              <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ListboxOption v-for="filter in filters" :key="filter.value" v-slot="{ active, selected }" as="template" :value="filter">
                  <li :class="[active ? 'bg-primary-600 text-white' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-3 pr-9']">
                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ filter.label }}</span>

                    <span v-if="selected" :class="[active ? 'text-white' : 'text-primary-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>
    </div>
    <section class="mt-8 pb-16">
      <h2 id="gallery-heading" class="sr-only">Pending Orders</h2>
      <ul class="z-10 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-8">
        <li
          v-for="custom in orders"
          :key="custom.id"
          :class="{
            'ring-primary-200': !custom.is_walmart && !custom.blemish,
            'ring-red-500': custom.blemish,
            'ring-blue-500': custom.is_walmart,
          }"
          class="group aspect-h-10 grid w-full grid-cols-2 gap-4 rounded-lg p-4 ring-2">
          <div>
            <img v-if="custom.data[0]" :src="custom.data[0].filename" alt="" class="group-hover:opacity-75" />
            <img v-else src="https://via.placeholder.com/300x300?text=No+Image" alt="" class="group-hover:opacity-75" />
          </div>
          <div class="space-y-4">
            <div>
              <Link :href="$route('orders.show', custom.order_number)" class="mt-2 block truncate text-sm font-medium text-gray-900">Order #{{ custom.order_number }}</Link>
              <p class="pointer-events-none block truncate text-sm font-medium text-gray-700">Quantity: {{ quantity(custom) }}</p>
              <p class="pointer-events-none flex items-center text-sm font-medium text-gray-500">
                {{ custom.ordered_at_formatted }}
              </p>
            </div>

            <button v-if="custom.status === 'pending' || custom.status === 'rejected'" type="button" class="btn btn-success btn-xs btn-block" @click="approveOrder(custom)">Approve</button>
            <button v-if="custom.status === 'pending' || custom.status === 'approved'" type="button" class="btn btn-danger btn-xs btn-block" @click="rejectOrder(custom)">Hold</button>
            <button v-if="custom.status === 'rejected'" type="button" class="btn btn-danger btn-xs btn-block" @click="cancelOrder(custom)">Cancel</button>
            <button v-if="custom.status === 'rejected' || custom.status === 'approved'" type="button" class="btn btn-primary btn-xs btn-block" @click="resetOrder(custom)">Reset</button>
          </div>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import { Link } from "@inertiajs/vue3";

import { deleteData, exportData, fetchData, putData } from "../../composables/CustomOrders";

export default {
  name: "CustomDecks",
  components: {
    CheckIcon,
    ChevronUpDownIcon,
    ListboxOption,
    ListboxOptions,
    ListboxButton,
    ListboxLabel,
    Listbox,
    Link,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tabs: [
        { name: "Pending Orders", slug: "pending", current: true },
        { name: "On Hold", slug: "rejected", current: false },
        { name: "Approved", slug: "approved", current: false },
      ],
      filters: [
        { label: "All", value: "all" },
        { label: "Skateboard", value: "skateboard" },
        { label: "Cruiser", value: "cruiser" },
        { label: "Drop Thru", value: "drop-thru" },
        { label: "EGG1", value: "EGG1" },
        { label: "Mini", value: "mini" },
        { label: "Pintail", value: "pintail" },
        { label: "SHP1", value: "SHP1" },
      ],
      selectedFilter: { label: "All", value: "all" },
      selectedStatus: "pending",
      orders: [],
      currentOrder: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    customsType() {
      const words = this.type.replace("-", " ").split(" ");
      return words
        .map((word) => {
          return word[0].toUpperCase() + word.substring(1);
        })
        .join(" ");
    },
  },
  mounted() {
    this.loading = true;
    this.selectStatus("pending");
  },
  methods: {
    quantity(custom) {
      if (custom.blemish > 0) {
        return custom.blemish;
      }

      return custom.product_quantity;
    },
    selectStatus(status) {
      this.selectedStatus = status;
      this.loading = true;
      fetchData(this.type, status, this.selectedFilter.value)
        .then((data) => {
          this.orders = data;
          this.tabs.forEach((tab) => {
            tab.current = false;
          });
          this.tabs.map((tab) => {
            if (tab.slug === status) {
              tab.current = true;
            }
          });
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    selectFilter(filter) {
      this.loading = true;
      fetchData(this.type, this.selectedStatus, filter.value)
        .then((data) => (this.orders = data))
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    approveOrder(order) {
      this.loading = true;
      putData(order, "approved")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    rejectOrder(order) {
      this.loading = true;
      putData(order, "rejected")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancelOrder(order) {
      this.loading = true;
      deleteData(order)
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetOrder(order) {
      this.loading = true;
      putData(order, "pending")
        .then(() => {
          this.orders = this.orders.filter(({ id }) => id !== order.id);
        })
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    viewLargerImage(image) {
      window.open(image, "_blank");
    },
    exportCsv() {
      this.loading = true;
      exportData(this.type)
        .catch((err) => {
          this.error = err;
          if (err.json) {
            return err.json.then((json) => {
              this.error.message = json.message;
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
